import React from 'react';
import { apiFetcher } from './useApiFetcher';
import { IApiRequest, IApiResponse } from '@/hooks/api/useApi/useApi.interfaces';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@app/i18n/useTranslationClient';
import { NOT_AUTH_HTTP_RESPONSE_CODE, authInterceptor, getTokenClient } from '@packages/authenticator';
import { translate } from '@/translations/static/staticTranslations';
import { addClientHttpCalls } from '@/services/fetchLogger/fetchClientLogger.service';
import { isDevToolbarEnabled } from '@/services/fetchLogger/fetchLogger.service';

const REWRITES_PREFIX = '/api';

// mostly for data sending on client side
export function useApiClient<response extends IApiResponse>(): {
    fetch: (request: IApiRequest) => Promise<response>;
    loading: boolean;
} {
    const router = useRouter();
    const {
        i18n: { language },
    } = useTranslation();
    const token = getTokenClient();
    const [loading, setIsLoading] = React.useState<boolean>(false);

    const fetch = React.useCallback(async (request: IApiRequest) => {
        setIsLoading(true);

        const rewritesPrefixedRequest: IApiRequest = {
            ...request,
            url: `${REWRITES_PREFIX}${request.url}`,
        };

        const response = await apiFetcher<response>(rewritesPrefixedRequest, language, token);

        if (isDevToolbarEnabled) {
            addClientHttpCalls(request, response);
        }

        if (response.status === NOT_AUTH_HTTP_RESPONSE_CODE) {
            authInterceptor(() => router.push(translate(language, 'routes.login')));
        }

        if (response.status === 404) {
            const redirectPath: string = translate(language, 'routes.notFound');
            router.push(redirectPath);
        }

        if (response.status === 500) {
            const redirectPath: string = translate(language, 'routes.internalError');
            router.push(redirectPath);
        }

        setIsLoading(false);

        return response;
    }, []);

    return {
        fetch,
        loading,
    };
}
